/**
 * "?" hints (docs/help/HELP_SECTION_PLAN.md §6). Each id is used once as
 * `<HelpHint id="…" />` and as the wrapper's `data-help` attribute, which the
 * screenshot specs use as a stable target. `guide#anchor` must be a real Help
 * heading id: `npm run check:help` fails otherwise.
 *
 * Text is general wording with no data in it, so it is fine in the bundle.
 */

export interface HelpHintEntry {
  /** One or two plain sentences. */
  text: string;
  /** Guide slug (content/help/guides/<slug>). */
  guide: string;
  /** Heading id in that guide (server/help/headings.ts slugifyHeading). */
  anchor: string;
}

export const HELP_HINTS = {
  // ── Pricebook Ingestion ─────────────────────────────────────────────────────
  'pricebook.department': {
    text: 'Every row in this upload, and every material made from it, is tagged Slab or Tile. You pick it on the Upload step.',
    guide: 'upload-a-pricebook',
    anchor: '1-pick-the-vendor-slab-or-tile-and-the-file',
  },
  'pricebook.item-number-and-sku': {
    text: "Item Number must be the vendor's product code, never a color or description. Map the vendor's SKU column to Vendor SKU: Lithos matches rows by Vendor SKU first, then Item Number.",
    guide: 'upload-a-pricebook',
    anchor: '2-map-the-columns',
  },
  'pricebook.mapping-views': {
    text: 'Table and Connections show the same mapping. Table has one row per pricebook field; Connections draws arrows from file columns to fields, and you drag a column onto a field.',
    guide: 'upload-a-pricebook',
    anchor: 'table-and-connections',
  },
  'pricebook.combine-columns': {
    text: 'The column you pick is added after the ones already in this field, joined by the separator (a space unless you type one). Check the e.g. line before you go on.',
    guide: 'upload-a-pricebook',
    anchor: 'combine-columns',
  },
  'pricebook.before-preview': {
    text: 'What still needs doing before Preview. Blocking keeps Next off; Review required and Warning are worth a look first. Click a name to jump to it.',
    guide: 'upload-a-pricebook',
    anchor: 'before-preview',
  },
  'pricebook.vendor-column': {
    text: "The file's Vendor column is not imported: every row imports under the vendor you picked on Upload. Lithos compares the two so you can check you picked the right vendor.",
    guide: 'upload-a-pricebook',
    anchor: 'before-preview',
  },
  'pricebook.record-matching': {
    text: 'How rows find the items the vendor already has: by Vendor SKU first, then by the calculated key when the SKU is blank. A match updates the item; anything else is added as new.',
    guide: 'upload-a-pricebook',
    anchor: 'record-matching',
  },
  'pricebook.review-classification': {
    text: 'Rows with a tile class code always import as Tile, whatever you picked. Accept that, or go back and change your selection.',
    guide: 'upload-a-pricebook',
    anchor: 'review-classification',
  },
  'pricebook.check-item-number': {
    text: 'The column mapped to Item Number looks like colors or words, or many rows share one number. Point Item Number back at the product-code column, or products get merged or duplicated.',
    guide: 'upload-a-pricebook',
    anchor: '3-if-you-see-check-item-number',
  },
  'pricebook.same-file': {
    text: "You already imported this exact file. Importing it again changes nothing unless the data was edited since, so check you didn't mean a newer file.",
    guide: 'upload-a-pricebook',
    anchor: '4-read-the-preview',
  },
  'pricebook.possible-duplicates': {
    text: 'Few rows in this file match the rows the vendor already has, so the rest would be added as new items. This usually means the wrong column was picked for Item Number or Vendor SKU.',
    guide: 'upload-a-pricebook',
    anchor: '4-read-the-preview',
  },
  'pricebook.columns-that-change': {
    text: "The columns this file changes on items already in the pricebook. Click one to see each row's old and new value.",
    guide: 'upload-a-pricebook',
    anchor: '4-read-the-preview',
  },
  'pricebook.matched-by': {
    text: 'How each row was identified: by Vendor SKU, by the calculated key (no SKU), or new when it matches nothing the vendor already has.',
    guide: 'upload-a-pricebook',
    anchor: 'matched-by',
  },
  'pricebook.rejected-rows': {
    text: 'Rows that are skipped, by their row number in your file, with the reason. The other rows still import. Export errors downloads the whole list.',
    guide: 'upload-a-pricebook',
    anchor: 'rows-that-will-not-import',
  },
  'pricebook.final-confirm': {
    text: 'Check the department, the vendor and the number of rows. The import is all or nothing, and nothing is saved until you click Yes, import in the last check.',
    guide: 'upload-a-pricebook',
    anchor: '5-import',
  },

  // ── Import History ──────────────────────────────────────────────────────────
  'import-history.fix-buttons': {
    text: 'Undo puts everything back the way it was before this upload. Fix & re-import reimports the same file with one column fixed. Delete removes only what this upload added.',
    guide: 'undo-a-pricebook-upload',
    anchor: 'which-button-to-use',
  },
  'import-history.status': {
    text: 'Imported means the rows are in the pricebook. Undone, Replaced (by Fix & re-import) and Data deleted mean someone used one of the Fix a bad upload buttons on it.',
    guide: 'undo-a-pricebook-upload',
    anchor: 'which-button-to-use',
  },

  // ── PO Tracker ──────────────────────────────────────────────────────────────
  'po-tracker.stock-vs-pos': {
    text: 'Stock groups material by color and shows what the lots need, what is ordered, in stock and still missing. POs lists the purchase orders, grouped by job, one row per PO.',
    guide: 'po-tracker',
    anchor: 'which-tab-to-use',
  },
  'po-tracker.template-window': {
    text: 'Lots that template within the next 14 days and still have something to order. Each such color gets an indicator: red not ordered, amber PO still Draft, violet under-ordered.',
    guide: 'po-tracker',
    anchor: 'stock-what-still-needs-ordering',
  },
  'po-tracker.colors-ordered': {
    text: "How many of the colors this job's lots need have at least one PO on the job. It counts POs, not quantities: use the Stock view for those.",
    guide: 'po-tracker',
    anchor: 'colors-ordered-badge',
  },
  'po-tracker.backorder': {
    text: 'Every PO line with the status Backorder, on any PO. Expected Delivery is the ETA; Not set means nobody has entered one yet.',
    guide: 'po-tracker',
    anchor: 'chase-backorders',
  },
  'po-tracker.backorder-eta': {
    text: "The date the vendor expects to ship these lines. It becomes their expected delivery date on the Backorder tab; leave it empty if you don't know.",
    guide: 'po-tracker',
    anchor: 'set-lines-to-backorder-with-an-eta',
  },
  'po-tracker.batch-actions': {
    text: 'Set Expected Delivery Date puts one date on every selected PO. Update Status changes them all and first asks what to do with their line items.',
    guide: 'po-tracker',
    anchor: 'change-several-pos-at-once',
  },
  'po-tracker.status-cascade': {
    text: 'Each line is set to the matching line status; change any you need. Update PO only leaves the lines alone, and Cancel changes nothing.',
    guide: 'po-tracker',
    anchor: 'change-a-status',
  },

  // ── PO sidebar ──────────────────────────────────────────────────────────────
  'po-sidebar.status': {
    text: "Click to change the PO's status. Lithos then asks whether to set its line items to match.",
    guide: 'po-tracker',
    anchor: 'change-a-status',
  },
  'po-sidebar.receive': {
    text: 'Record what arrived on this PO, all of it or only part. Lithos creates an inventory serial for each slab or unit.',
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },

  // ── Receive dialog (office) ─────────────────────────────────────────────────
  'receive.quantity': {
    text: "How many arrived, up to Remaining. Put 0 on a line that didn't come; what you don't receive now stays open for later.",
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },
  'receive.preprinted': {
    text: 'Labels already printed for this line with Pre-Print Barcodes and not received yet. Receiving uses them first, lowest serial first, so their serials go on what arrives.',
    guide: 'receive-a-po',
    anchor: 'if-labels-were-pre-printed',
  },
  'receive.serials': {
    text: 'Serials Lithos makes on top of the pre-printed labels this receipt uses: Quantity to Receive minus those labels. 0 is fine: the quantity is still received, just with no new serials.',
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },
  'receive.block-number': {
    text: "The vendor's block number from the slab tags. One per line: it goes on every slab from this line.",
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },
  'receive.slab-size': {
    text: "Type each slab's actual size. Apply to all copies one size to every slab; then fix the odd slab that differs.",
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },
  'receive.size-difference': {
    text: "This slab's size differs from the order; the badge shows the difference in sqft and dollars. After you receive, Lithos drafts an email for you to send the vendor.",
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },
  'receive.partial-note': {
    text: 'A note on each short line says what is still to come, so everyone can see it. Skip saves nothing.',
    guide: 'receive-a-po',
    anchor: 'how-to-receive-at-a-desk',
  },

  // ── Field app: Warehouse → Receive ─────────────────────────────────────────
  // On the pick-a-PO step, before anything is typed: Learn more leaves the page.
  'field-receive.steps': {
    text: "You go through the PO one line at a time: turn on Received this item for each line that arrived and enter how much came. Lines you leave off stay open to receive later; Damaged / short is for noting what's wrong. If labels were pre-printed for a line, it shows how many; receiving uses those first, then creates new serials for the rest.",
    guide: 'receive-a-po',
    anchor: 'how-to-receive-on-a-phone-or-tablet',
  },
} as const satisfies Record<string, HelpHintEntry>;

export type HelpHintId = keyof typeof HELP_HINTS;
